import { FC, ReactNode } from 'react';
import cx from 'classnames';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useInjuryEndpoints } from '../../../hooks/useInjuryEndpoints';
import { ATHLETE_INJURY_METRIC } from '../../../enums/injuries';
import LeopartLoader from '../../../shared/LeopartLoader/LeopartLoader';
import Typhography from '../../../shared/Typhography/Typhography';
import {
  IAthleteCapabilities,
  ITimelineMetric,
  IPainNrsMetric,
  IBodyMapMetric,
  IRecurrenceMapMetric,
  ILoadAtInjuryMetric,
  IReturnConfidenceMetric,
  IDaysLostSeverityMetric,
  ITypeBreakdownMetric,
  IContextBreakdownMetric,
  IMonthlyRateMetric,
  IRtpProgressMetric,
  IReinjuryRiskMetric,
  IMechanismBreakdownMetric,
  IAcwrSeriesMetric,
  ISrpeWeeklyMetric,
  IReadinessIndexMetric,
  IHooperSeriesMetric,
  ITonnageSeriesMetric,
  IExposureHoursMetric,
  ICmjRsiSeriesMetric,
  IHrvSeriesMetric,
  ILoadProgressionMetric,
  IInsightsMetric,
} from '../../../models/injury';

import { ChartErrorState } from './charts/_shared/ChartErrorState';
import { InjuryTimelineOverlay } from './charts/InjuryTimelineOverlay';
import { PainNrsChart } from './charts/PainNrsChart';
import { BodyMapChart } from './charts/BodyMapChart';
import { RecurrenceMapChart } from './charts/RecurrenceMapChart';
import { LoadAtInjuryChart } from './charts/LoadAtInjuryChart';
import { ReturnConfidenceChart } from './charts/ReturnConfidenceChart';
import { DaysLostSeverityChart } from './charts/DaysLostSeverityChart';
import { TypeBreakdownChart } from './charts/TypeBreakdownChart';
import { ContextBreakdownChart } from './charts/ContextBreakdownChart';
import { MonthlyRateChart } from './charts/MonthlyRateChart';
import { RtpProgressChart } from './charts/RtpProgressChart';
import { ReinjuryRiskCard } from './charts/ReinjuryRiskCard';
import { MechanismBreakdownChart } from './charts/MechanismBreakdownChart';
import { AcwrSeriesChart } from './charts/AcwrSeriesChart';
import { SrpeWeeklyChart } from './charts/SrpeWeeklyChart';
import { ReadinessIndexChart } from './charts/ReadinessIndexChart';
import { HooperSeriesChart } from './charts/HooperSeriesChart';
import { TonnageSeriesChart } from './charts/TonnageSeriesChart';
import { ExposureHoursChart } from './charts/ExposureHoursChart';
import { CmjRsiSeriesChart } from './charts/CmjRsiSeriesChart';
import { HrvSeriesChart } from './charts/HrvSeriesChart';
import { LoadProgressionChart } from './charts/LoadProgressionChart';
import styles from './AthleteInjuryCharts.module.scss';

interface AthleteInjuryChartsProps {
  athleteId: string;
  period?: string;
  insights?: IInsightsMetric;
}

// Card wrapper comune per ogni chart: unico punto in cui viene stampato il
// titolo, i chart figli non lo duplicano più (vedi charts/*.tsx).
const ChartCard: FC<{ title: string; children: ReactNode; fullWidth?: boolean }> = ({ title, children, fullWidth }) => (
  <div className={cx(styles.chartCard, { [styles.chartCardFullWidth]: fullWidth })}>
    <div className={styles.chartCardHeader}>
      <Typhography type="span" size="medium-14" className={styles.chartCardTitle}>
        {title}
      </Typhography>
    </div>
    {children}
  </div>
);

// Hook generico per una singola metrica
function useMetric<T>(
  athleteId: string,
  metric: ATHLETE_INJURY_METRIC,
  period: string | undefined,
  enabled: boolean
) {
  const endpoints = useInjuryEndpoints();
  return useQuery<T>({
    queryKey: ['injury', 'athlete', athleteId, 'metric', metric, period ?? 'all'],
    queryFn: async () => {
      const { data } = await endpoints.getAthleteInjuryMetric(athleteId, metric, period ? { period } : undefined);
      return data as T;
    },
    enabled,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Contenuto di una singola card: loading/error/dato sono tre stati distinti
 * (bug corretto: prima il loading passava `data ?? null` al chart, che lo
 * renderizzava come "Nessun dato disponibile" invece di mostrare un loader).
 */
function renderMetricPanel<T>(
  query: UseQueryResult<T>,
  title: string,
  Chart: FC<{ data: T | null; period?: string }>,
  period: string | undefined,
  fullWidth?: boolean
) {
  return (
    <ChartCard key={title} title={title} fullWidth={fullWidth}>
      {query.isLoading ? (
        <div className={styles.chartLoading}>
          <LeopartLoader size="sm" />
        </div>
      ) : query.isError ? (
        <ChartErrorState onRetry={query.refetch} message={(query.error as Error)?.message} />
      ) : (
        <Chart data={query.data ?? null} period={period} />
      )}
    </ChartCard>
  );
}

export const AthleteInjuryCharts: FC<AthleteInjuryChartsProps> = ({ athleteId, period }) => {
  const endpoints = useInjuryEndpoints();

  // 1. Capabilities
  const { data: capabilities, isLoading: capsLoading, isError: capsError, refetch: capsRefetch } = useQuery<IAthleteCapabilities>({
    queryKey: ['injury', 'athlete', athleteId, 'capabilities'],
    queryFn: async () => {
      const { data } = await endpoints.getAthleteCapabilities(athleteId);
      return data;
    },
    staleTime: 1000 * 60 * 10,
  });

  const has = (m: ATHLETE_INJURY_METRIC) => capabilities?.[m] === true;

  // 2. Queries per ogni metrica (condizionali su capabilities)
  const timelineQ = useMetric<ITimelineMetric>(athleteId, ATHLETE_INJURY_METRIC.TIMELINE, period, has(ATHLETE_INJURY_METRIC.TIMELINE));
  const painNrsQ = useMetric<IPainNrsMetric>(athleteId, ATHLETE_INJURY_METRIC.PAIN_NRS, period, has(ATHLETE_INJURY_METRIC.PAIN_NRS));
  const bodyMapQ = useMetric<IBodyMapMetric>(athleteId, ATHLETE_INJURY_METRIC.BODY_MAP, period, has(ATHLETE_INJURY_METRIC.BODY_MAP));
  const recurrenceQ = useMetric<IRecurrenceMapMetric>(athleteId, ATHLETE_INJURY_METRIC.RECURRENCE_MAP, period, has(ATHLETE_INJURY_METRIC.RECURRENCE_MAP));
  const loadAtInjuryQ = useMetric<ILoadAtInjuryMetric>(athleteId, ATHLETE_INJURY_METRIC.LOAD_AT_INJURY, period, has(ATHLETE_INJURY_METRIC.LOAD_AT_INJURY));
  const returnConfQ = useMetric<IReturnConfidenceMetric>(athleteId, ATHLETE_INJURY_METRIC.RETURN_CONFIDENCE, period, has(ATHLETE_INJURY_METRIC.RETURN_CONFIDENCE));
  const daysLostQ = useMetric<IDaysLostSeverityMetric>(athleteId, ATHLETE_INJURY_METRIC.DAYS_LOST_SEVERITY, period, has(ATHLETE_INJURY_METRIC.DAYS_LOST_SEVERITY));
  const typeQ = useMetric<ITypeBreakdownMetric>(athleteId, ATHLETE_INJURY_METRIC.TYPE_BREAKDOWN, period, has(ATHLETE_INJURY_METRIC.TYPE_BREAKDOWN));
  const contextQ = useMetric<IContextBreakdownMetric>(athleteId, ATHLETE_INJURY_METRIC.CONTEXT_BREAKDOWN, period, has(ATHLETE_INJURY_METRIC.CONTEXT_BREAKDOWN));
  const monthlyQ = useMetric<IMonthlyRateMetric>(athleteId, ATHLETE_INJURY_METRIC.MONTHLY_RATE, period, has(ATHLETE_INJURY_METRIC.MONTHLY_RATE));
  const rtpQ = useMetric<IRtpProgressMetric>(athleteId, ATHLETE_INJURY_METRIC.RTP_PROGRESS, period, has(ATHLETE_INJURY_METRIC.RTP_PROGRESS));
  const riskQ = useMetric<IReinjuryRiskMetric>(athleteId, ATHLETE_INJURY_METRIC.REINJURY_RISK, period, has(ATHLETE_INJURY_METRIC.REINJURY_RISK));
  const mechanismQ = useMetric<IMechanismBreakdownMetric>(athleteId, ATHLETE_INJURY_METRIC.MECHANISM_BREAKDOWN, period, has(ATHLETE_INJURY_METRIC.MECHANISM_BREAKDOWN));
  const acwrQ = useMetric<IAcwrSeriesMetric>(athleteId, ATHLETE_INJURY_METRIC.ACWR_SERIES, period, has(ATHLETE_INJURY_METRIC.ACWR_SERIES));
  const srpeQ = useMetric<ISrpeWeeklyMetric>(athleteId, ATHLETE_INJURY_METRIC.SRPE_WEEKLY, period, has(ATHLETE_INJURY_METRIC.SRPE_WEEKLY));
  const readinessQ = useMetric<IReadinessIndexMetric>(athleteId, ATHLETE_INJURY_METRIC.READINESS_INDEX, period, has(ATHLETE_INJURY_METRIC.READINESS_INDEX));
  const hooperQ = useMetric<IHooperSeriesMetric>(athleteId, ATHLETE_INJURY_METRIC.HOOPER_SERIES, period, has(ATHLETE_INJURY_METRIC.HOOPER_SERIES));
  const tonnageQ = useMetric<ITonnageSeriesMetric>(athleteId, ATHLETE_INJURY_METRIC.TONNAGE_SERIES, period, has(ATHLETE_INJURY_METRIC.TONNAGE_SERIES));
  const exposureQ = useMetric<IExposureHoursMetric>(athleteId, ATHLETE_INJURY_METRIC.EXPOSURE_HOURS, period, has(ATHLETE_INJURY_METRIC.EXPOSURE_HOURS));
  const cmjRsiQ = useMetric<ICmjRsiSeriesMetric>(athleteId, ATHLETE_INJURY_METRIC.CMJ_RSI_SERIES, period, has(ATHLETE_INJURY_METRIC.CMJ_RSI_SERIES));
  const hrvQ = useMetric<IHrvSeriesMetric>(athleteId, ATHLETE_INJURY_METRIC.HRV_SERIES, period, has(ATHLETE_INJURY_METRIC.HRV_SERIES));
  const loadProgQ = useMetric<ILoadProgressionMetric>(athleteId, ATHLETE_INJURY_METRIC.LOAD_PROGRESSION, period, has(ATHLETE_INJURY_METRIC.LOAD_PROGRESSION));

  if (capsLoading) {
    return (
      <div className={styles.loadingRow}>
        <LeopartLoader size="sm" />
        Caricamento grafici...
      </div>
    );
  }

  if (capsError) {
    return <ChartErrorState onRetry={capsRefetch} message="Impossibile verificare le metriche disponibili" />;
  }

  // Non mostriamo nulla se non ci sono metriche disponibili
  if (!capabilities || !Object.values(capabilities).some(Boolean)) return null;

  return (
    <div>
      <div className={styles.header}>
        <Typhography type="h3" size="bold-16" className={styles.title}>
          Analisi infortuni
        </Typhography>
      </div>

      <div className={styles.grid}>
        {has(ATHLETE_INJURY_METRIC.TIMELINE) &&
          renderMetricPanel(timelineQ, 'Timeline readiness + infortuni', InjuryTimelineOverlay, period, true)}

        {has(ATHLETE_INJURY_METRIC.ACWR_SERIES) &&
          renderMetricPanel(acwrQ, 'ACWR nel tempo', AcwrSeriesChart, period, true)}

        {has(ATHLETE_INJURY_METRIC.HOOPER_SERIES) &&
          renderMetricPanel(hooperQ, 'Questionario Hooper', HooperSeriesChart, period, true)}

        {has(ATHLETE_INJURY_METRIC.PAIN_NRS) &&
          renderMetricPanel(painNrsQ, 'Dolore (NRS)', PainNrsChart, period)}

        {has(ATHLETE_INJURY_METRIC.READINESS_INDEX) &&
          renderMetricPanel(readinessQ, 'Indice di readiness', ReadinessIndexChart, period)}

        {has(ATHLETE_INJURY_METRIC.HRV_SERIES) &&
          renderMetricPanel(hrvQ, 'HRV', HrvSeriesChart, period)}

        {has(ATHLETE_INJURY_METRIC.CMJ_RSI_SERIES) &&
          renderMetricPanel(cmjRsiQ, 'CMJ & RSI', CmjRsiSeriesChart, period)}

        {has(ATHLETE_INJURY_METRIC.SRPE_WEEKLY) &&
          renderMetricPanel(srpeQ, 'Carico soggettivo settimanale (sRPE)', SrpeWeeklyChart, period)}

        {has(ATHLETE_INJURY_METRIC.TONNAGE_SERIES) &&
          renderMetricPanel(tonnageQ, 'Tonnellaggio settimanale', TonnageSeriesChart, period)}

        {has(ATHLETE_INJURY_METRIC.LOAD_PROGRESSION) &&
          renderMetricPanel(loadProgQ, 'Progressione carico', LoadProgressionChart, period)}

        {has(ATHLETE_INJURY_METRIC.RTP_PROGRESS) &&
          renderMetricPanel(rtpQ, 'Protocollo RTP', RtpProgressChart, period)}

        {has(ATHLETE_INJURY_METRIC.REINJURY_RISK) &&
          renderMetricPanel(riskQ, 'Rischio recidiva', ReinjuryRiskCard, period)}

        {has(ATHLETE_INJURY_METRIC.RETURN_CONFIDENCE) &&
          renderMetricPanel(returnConfQ, 'Fiducia al ritorno', ReturnConfidenceChart, period)}

        {has(ATHLETE_INJURY_METRIC.BODY_MAP) &&
          renderMetricPanel(bodyMapQ, 'Giorni persi per zona', BodyMapChart, period)}

        {has(ATHLETE_INJURY_METRIC.RECURRENCE_MAP) &&
          renderMetricPanel(recurrenceQ, 'Recidive per zona', RecurrenceMapChart, period)}

        {has(ATHLETE_INJURY_METRIC.LOAD_AT_INJURY) &&
          renderMetricPanel(loadAtInjuryQ, "ACWR al momento dell'infortunio", LoadAtInjuryChart, period)}

        {has(ATHLETE_INJURY_METRIC.TYPE_BREAKDOWN) &&
          renderMetricPanel(typeQ, 'Tipo infortunio', TypeBreakdownChart, period)}

        {has(ATHLETE_INJURY_METRIC.CONTEXT_BREAKDOWN) &&
          renderMetricPanel(contextQ, 'Contesto infortunio', ContextBreakdownChart, period)}

        {has(ATHLETE_INJURY_METRIC.MECHANISM_BREAKDOWN) &&
          renderMetricPanel(mechanismQ, 'Meccanismo infortunio', MechanismBreakdownChart, period)}

        {has(ATHLETE_INJURY_METRIC.DAYS_LOST_SEVERITY) &&
          renderMetricPanel(daysLostQ, 'Giorni persi per severità', DaysLostSeverityChart, period)}

        {has(ATHLETE_INJURY_METRIC.MONTHLY_RATE) &&
          renderMetricPanel(monthlyQ, 'Infortuni mensili', MonthlyRateChart, period)}

        {has(ATHLETE_INJURY_METRIC.EXPOSURE_HOURS) &&
          renderMetricPanel(exposureQ, 'Ore di esposizione', ExposureHoursChart, period)}
      </div>
    </div>
  );
};
